import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import {
  useDocumentVisibility,
  useEventListener,
  useMemoizedFn,
  useRequest,
  useUpdateEffect,
} from 'ahooks';
import { apiGetAssistantConfig } from '@/api/assistant';
import {
  ASSISTANT_CONFIG_CHANGE_EVENT,
  ASSISTANT_CONFIG_POLL_MS,
} from '@/constants/business/assistant';
import { useOrg } from '@/utils/hooks/useOrg';
import { useStore } from '@/store';
import ChatWindow from './components/ChatWindow';
import assistantFabIcon from '@/assets/assistant-fab-icon.png';
import {
  getAssistantFabPos,
  getLastAssistantConversationId,
  setAssistantFabPos,
  setLastAssistantConversationId,
} from '@/utils/assistantStorage';
import styles from './index.module.less';

const FAB_SIZE = 64;
const NAV_HEIGHT = 56;
const FAB_EDGE = 8;

const clampFabOffset = (right: number, bottom: number) => ({
  right: Math.min(
    window.innerWidth - FAB_SIZE - FAB_EDGE,
    Math.max(FAB_EDGE, right)
  ),
  bottom: Math.min(
    window.innerHeight - FAB_SIZE - NAV_HEIGHT,
    Math.max(FAB_EDGE, bottom)
  ),
});

const AssistantChat = observer(() => {
  const { orgId } = useOrg();
  const { member, userLoading } = useStore('user');
  const { pathname } = useLocation();
  const documentVisibility = useDocumentVisibility();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [conversationId, setConversationId] = useState<string>();
  const [fabPos, setFabPos] = useState<
    { right: number; bottom: number } | undefined
  >(getAssistantFabPos);
  const dragRef = useRef<{
    dragging: boolean;
    moved: boolean;
    startX: number;
    startY: number;
    originRight: number;
    originBottom: number;
    lastPos?: { right: number; bottom: number };
  }>({
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    originRight: 0,
    originBottom: 0,
  });
  const skipClickRef = useRef(false);
  const canFetch = !userLoading && !!member && !!orgId;

  const { data: config, runAsync: fetchConfig } = useRequest(
    apiGetAssistantConfig,
    {
      ready: canFetch,
      refreshDeps: [orgId],
      pollingInterval: ASSISTANT_CONFIG_POLL_MS,
      pollingWhenHidden: false,
      onSuccess: (next) => {
        if (!next.enabled) {
          setOpen(false);
          setMounted(false);
        }
      },
    }
  );

  useEventListener(
    ASSISTANT_CONFIG_CHANGE_EVENT,
    () => {
      if (canFetch) {
        fetchConfig();
      }
    },
    { target: window }
  );

  useUpdateEffect(() => {
    if (documentVisibility === 'visible' && canFetch) {
      fetchConfig();
    }
  }, [documentVisibility, pathname]);

  useUpdateEffect(() => {
    setOpen(false);
    setMounted(false);
    setConversationId(undefined);
  }, [orgId]);

  const openChat = useMemoizedFn(async () => {
    if (!member || !orgId) {
      return;
    }
    const next = await fetchConfig();
    if (!next.enabled) {
      return;
    }
    if (!conversationId) {
      const lastId = getLastAssistantConversationId(member.memberId, orgId);
      if (lastId) {
        setConversationId(lastId);
      }
    }
    setMounted(true);
    setOpen(true);
  });

  const handleConversationChange = useMemoizedFn((id?: string) => {
    setConversationId(id);
    if (id && member && orgId) {
      setLastAssistantConversationId(member.memberId, orgId, id);
    }
  });

  const onPointerDown = useMemoizedFn(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      dragRef.current = {
        dragging: true,
        moved: false,
        startX: event.clientX,
        startY: event.clientY,
        originRight: window.innerWidth - rect.right,
        originBottom: window.innerHeight - rect.bottom,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  );

  const onPointerMove = useMemoizedFn(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!dragRef.current.dragging) {
        return;
      }
      const dx = event.clientX - dragRef.current.startX;
      const dy = event.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        dragRef.current.moved = true;
      }
      const next = clampFabOffset(
        dragRef.current.originRight - dx,
        dragRef.current.originBottom - dy
      );
      dragRef.current.lastPos = next;
      setFabPos(next);
    }
  );

  const onPointerUp = useMemoizedFn(() => {
    if (dragRef.current.moved) {
      skipClickRef.current = true;
      if (dragRef.current.lastPos) {
        setAssistantFabPos(dragRef.current.lastPos);
      }
    }
    dragRef.current.dragging = false;
  });

  const onFabClick = useMemoizedFn(() => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    openChat();
  });

  if (!canFetch || !config?.enabled) {
    return null;
  }

  const fabStyle = fabPos
    ? {
        right: `min(${fabPos.right}px, calc(100vw - ${FAB_SIZE + FAB_EDGE}px))`,
        bottom: `min(${fabPos.bottom}px, calc(100vh - ${
          FAB_SIZE + NAV_HEIGHT
        }px))`,
        left: 'auto',
        top: 'auto',
      }
    : undefined;

  return (
    <>
      {!open && (
        <button
          type="button"
          className={styles.fab}
          style={fabStyle}
          onClick={onFabClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <img
            className={styles.fabIcon}
            src={assistantFabIcon}
            alt=""
            draggable={false}
          />
        </button>
      )}
      {mounted && (
        <ChatWindow
          config={config}
          visible={open}
          conversationId={conversationId}
          onConversationChange={handleConversationChange}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
});

export default AssistantChat;
